import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../features/auth/services/auth.service';
import { HttpService } from '../services/http.service';
import { OperadorService, codigoDeOperador } from '../operador/operador.service';
import { BaseLocal } from './base-local';

/** Un cambio del técnico que todavía no llegó al servidor. */
export interface OperacionPendiente {
  /** UUID generado en la tablet: el backend deduplica por él (RFC §2.7). */
  clientOpId: string;
  metodo: 'POST' | 'PUT' | 'DELETE';
  /** Relativa a la API, como en `HttpService`: `/ordenes-trabajo/15/materiales`. */
  ruta: string;
  /** Cuerpo JSON. Se le agrega `clientOpId` al enviarlo. */
  cuerpo?: Record<string, unknown>;
  /** Una foto o firma: se manda como multipart, con `campos` al lado. */
  archivo?: { campo: string; blob: Blob; nombre: string };
  campos?: Record<string, string>;
  /** Qué es, en palabras del técnico, para la cola visible («Foto del horómetro»). */
  descripcion: string;
  /**
   * A qué cosa de la pantalla pertenece (por ejemplo, la ruta de un ítem del checklist). Solo la usa la tablet para
   * mostrar el estado donde el técnico lo hizo: no se manda al servidor.
   */
  ref?: string;
  /**
   * Un segundo pedido que depende del resultado de este: cuando el servidor acepta el primero, se manda con el
   * `id` que devolvió puesto en `cuerpo[campo]`. Es lo que liga una foto a su ítem sin carreras en la cola: las dos
   * cosas viajan juntas y en orden. Si el segundo falla, se reintenta todo; el servidor deduplica la foto por su
   * `clientOpId`, así que no se sube dos veces.
   */
  luego?: { metodo: 'PUT' | 'POST'; ruta: string; cuerpo: Record<string, unknown>; campo: string };
  /**
   * Fotos que dependen del resultado de este pedido: cuando el servidor lo acepta, cada una se sube ligada al `id` que
   * devolvió (`campoId`). Es lo que liga la foto de un repuesto al hallazgo recién creado sin esperar a tener señal para tomarla.
   * Cada foto lleva su propio `clientOpId` derivado, así que reintentar no la sube dos veces.
   */
  luegoFotos?: { ruta: string; campoId: string; campos?: Record<string, string>; fotos: { blob: Blob; nombre: string; descripcion?: string }[] };
  /**
   * El mecánico que tenía la tablet cuando se hizo (si el taller usa PIN). Sale solo cuando la tablet la tiene de nuevo él: si
   * saliera con la sesión de otro, el cambio quedaría a nombre de quien no lo hizo.
   */
  operadorId?: number;
  /** Orden total de creación. Date.now() por sí solo puede repetirse. */
  creadaEn: number;
  intentos: number;
  /**
   * `pendiente`: se reintenta sola. `rechazada`: el servidor dijo que no
   * (una regla de negocio); no se reintenta hasta que alguien decida.
   */
  estado: 'pendiente' | 'rechazada';
  /** El mensaje del servidor, ya humano, cuando la rechazó. */
  error?: string;
}

/** WebKit puede fallar al clonar Blob multimedia en IndexedDB; almacenamos bytes. */
type OperacionAlmacenada = Omit<OperacionPendiente, 'archivo' | 'luegoFotos'> & {
  archivo?: { campo: string; blob: ArrayBuffer; tipo: string; nombre: string };
  luegoFotos?: { ruta: string; campoId: string; campos?: Record<string, string>;
    fotos: { blob: ArrayBuffer; tipo: string; nombre: string; descripcion?: string }[] };
};

export type NuevaOperacion = Pick<OperacionPendiente, 'metodo' | 'ruta' | 'descripcion'>
  & Partial<Pick<OperacionPendiente, 'clientOpId' | 'cuerpo' | 'archivo' | 'campos' | 'ref' | 'luego' | 'luegoFotos'>>;

/**
 * Estado combinado, para quien solo necesita un valor (compatibilidad). La
 * pantalla usa las dos dimensiones por separado: {@link Conexion} y
 * {@link Sincronizacion}.
 */
export type EstadoConexion = 'en-linea' | 'sin-conexion' | 'sincronizando' | 'con-pendientes' | 'con-error';

/**
 * ¿Llegamos al servidor? No es lo mismo que «hay Wi-Fi»: el navegador puede
 * estar conectado y el backend no responder. Se decide con dos señales: la red
 * del navegador y la última respuesta real del servidor (status 0 = no llegó).
 */
export type Conexion = 'en-linea' | 'sin-conexion';

/**
 * ¿Qué pasa con los cambios de esta tablet? Independiente de la conexión:
 * sin conexión puede haber cero pendientes, y en línea puede haber un rechazo.
 * - `sin-cambios`: todo llegó.
 * - `pendientes`: hay cambios guardados aquí esperando su turno.
 * - `sincronizando`: se están enviando ahora.
 * - `error`: el servidor rechazó un cambio (regla de negocio), o está en línea
 *   y aun así no acepta los envíos (5xx, sesión vencida). Requiere atención.
 */
export type Sincronizacion = 'sin-cambios' | 'pendientes' | 'sincronizando' | 'error';

/** Por qué falló el último envío, para decirlo en palabras del técnico. */
export type MotivoFallo = 'red' | 'servidor' | 'sesion';

interface Borrador {
  clave: string;
  valor: unknown;
  guardadoEn: number;
  /**
   * `borrador`: trabajo escrito que todavía no se mandó (lo que se pierde si nadie lo envía). `copia`: algo que la
   * pantalla guarda para sí (lo que llegó del servidor, una marca de «ya visto»). Solo los borradores cuentan al
   * cerrar sesión. Sin tipo: guardado antes de que existiera, se clasifica por su clave ({@link esCopiaHeredada}).
   */
  tipo?: 'borrador' | 'copia';
}

/**
 * Lo que las pantallas del taller guardaban como borrador antes de que existiera {@link Borrador.tipo} y no es trabajo
 * sin enviar: copias de lo que llegó del servidor (la orden y su checklist, las líneas aprobadas, los materiales, los
 * tiempos, participantes y visita, la jornada, el diagnóstico tal cual: lo que falta mandar lo dice su `:pendiente`) y
 * marcas de la pantalla (etapa, último control, diagnóstico visto).
 *
 * <p>Es una lista cerrada a propósito: una clave que no está acá cuenta como borrador. Si un borrador real pasara por
 * copia, cerrar sesión borraría la base con él adentro. `materiales:flujo:<id>` sí es borrador; `materiales:<id>` no.
 */
export function esCopiaHeredada(clave: string): boolean {
  return /^lineas:\d+$/.test(clave)
    || /^materiales:\d+$/.test(clave)
    || clave.startsWith('jornada:copia:')
    || /^workspace:\d+(:(diagnostico|diagnostico:visto|operations|times|punto|step))?$/.test(clave);
}

/**
 * La base offline del expediente (ES-31, ES-32, RFC §2.7, #864).
 *
 * <p><b>Una base por tenant y usuario.</b> La tablet se comparte entre
 * técnicos: cada uno tiene su propia base IndexedDB, nombrada por el dominio y
 * el id del usuario. Al cambiar de sesión se cierra la anterior y se abre la
 * del que entra; nunca se ven ni se envían los pendientes de otro.
 *
 * <p><b>Outbox.</b> Cada cambio es una operación con su `clientOpId`, en el
 * orden en que se hizo. Se guarda primero y se envía después: sin señal se
 * sigue trabajando, y un cierre del navegador no pierde nada. Un reintento
 * nunca duplica, porque el backend deduplica por `clientOpId`.
 *
 * <p><b>Qué se reintenta y qué no.</b> Sin conexión, un 5xx, un 408 o un 429
 * son del camino: se reintenta con espera creciente. Un 4xx de negocio (409
 * por seguridad detenida, 400 por un dato faltante) no se arregla
 * reintentando: queda «rechazada» con el mensaje del servidor hasta que el
 * técnico la corrija o la descarte. La resolución avanzada de conflictos es
 * de una fase posterior.
 *
 * <p>No dibuja nada: el indicador y la cola son de la pantalla del técnico
 * (E14), que espera su diseño. Acá está el estado que esa pantalla muestra.
 */
@Injectable({ providedIn: 'root' })
export class ExpedienteOfflineService {

  private static readonly ESPERA_INICIAL_MS = 2_000;
  private static readonly ESPERA_MAXIMA_MS = 60_000;
  private static ultimaCreacion = 0;

  private readonly http = inject(HttpService);
  private readonly auth = inject(AuthService);
  private readonly operador = inject(OperadorService);

  private base: BaseLocal | null = null;
  private abriendo: Promise<void> | null = null;
  private espera = ExpedienteOfflineService.ESPERA_INICIAL_MS;
  private reintento: ReturnType<typeof setTimeout> | null = null;
  private enCurso: Promise<void> | null = null;
  /** La vuelta que sigue a la que está en curso, si alguien la pidió. */
  private siguiente: Promise<void> | null = null;
  /** Lo pendiente que la vuelta en curso lleva en su lista. */
  private enLaVuelta = new Set<string>();
  /** Ya se le pidió al navegador que no borre la base (se pide una vez por carga). */
  private persistenciaPedida = false;

  /** La red del navegador (`online`/`offline`). */
  private readonly _red = signal<Conexion>(
    typeof navigator === 'undefined' || navigator.onLine ? 'en-linea' : 'sin-conexion');
  /**
   * Si el servidor respondió la última vez que se le habló. Con Wi-Fi pero sin
   * salida a internet el navegador dice «online»; esto dice la verdad.
   */
  private readonly _servidorResponde = signal(true);
  private readonly _ultimoFallo = signal<MotivoFallo | null>(null);
  private readonly _sincronizando = signal(false);
  private readonly _operaciones = signal<OperacionPendiente[]>([]);

  /** Las operaciones de este usuario, en orden. */
  readonly operaciones = this._operaciones.asReadonly();
  /** Conectividad real: red del navegador y servidor que responde. Nada de la cola. */
  readonly conexion = computed<Conexion>(() =>
    this._red() === 'sin-conexion' || !this._servidorResponde() ? 'sin-conexion' : 'en-linea');
  /** Por qué no salió lo último, si algo falló. */
  readonly ultimoFallo = this._ultimoFallo.asReadonly();
  readonly pendientes = computed(() => this._operaciones().filter(o => o.estado === 'pendiente').length);
  readonly rechazadas = computed(() => this._operaciones().filter(o => o.estado === 'rechazada'));
  readonly fotosPendientes = computed(() =>
    this._operaciones().filter(o => o.archivo && o.estado === 'pendiente').length);
  readonly sincronizando = this._sincronizando.asReadonly();

  /** La cola, independiente de la conexión. */
  readonly sincronizacion = computed<Sincronizacion>(() => {
    if (this._sincronizando()) return 'sincronizando';
    if (this.rechazadas().length > 0) return 'error';
    const pendientes = this.pendientes();
    // En línea, con pendientes que el servidor no acepta (5xx, sesión vencida):
    // no es «esperando señal», es un problema que hay que decir.
    if (pendientes > 0 && this.conexion() === 'en-linea'
        && (this._ultimoFallo() === 'servidor' || this._ultimoFallo() === 'sesion')) return 'error';
    return pendientes > 0 ? 'pendientes' : 'sin-cambios';
  });

  readonly estado = computed<EstadoConexion>(() => {
    if (this.conexion() === 'sin-conexion') return 'sin-conexion';
    switch (this.sincronizacion()) {
      case 'sincronizando': return 'sincronizando';
      case 'error': return 'con-error';
      case 'pendientes': return 'con-pendientes';
      default: return 'en-linea';
    }
  });

  constructor() {
    const destroyRef = inject(DestroyRef);
    if (typeof window !== 'undefined') {
      const alVolver = () => {
        this._red.set('en-linea');
        // Volvió la red: se da al servidor por alcanzable y el primer envío lo
        // confirma o lo desmiente. Sin sondeo extra.
        this._servidorResponde.set(true);
        this.espera = ExpedienteOfflineService.ESPERA_INICIAL_MS;
        void this.sincronizar();
      };
      const alPerder = () => this._red.set('sin-conexion');
      // La tablet vuelve de estar bloqueada o en otra app: un intento, no un intervalo.
      const alMostrarse = () => {
        if (document.visibilityState === 'visible' && (this.pendientes() > 0 || !this._servidorResponde())) {
          this.espera = ExpedienteOfflineService.ESPERA_INICIAL_MS;
          void this.sincronizar();
        }
      };
      window.addEventListener('online', alVolver);
      window.addEventListener('offline', alPerder);
      document.addEventListener('visibilitychange', alMostrarse);
      destroyRef.onDestroy(() => {
        window.removeEventListener('online', alVolver);
        window.removeEventListener('offline', alPerder);
        document.removeEventListener('visibilitychange', alMostrarse);
        this.cancelarReintento();
        this.base?.cerrar();
      });
    }
    // Lo que quedó esperando a un mecánico sale en cuanto la tablet vuelve a estar lista para él.
    this.operador.desbloqueos$.pipe(takeUntilDestroyed(destroyRef))
      .subscribe(() => { if (this.pendientes() > 0) void this.sincronizar(); });
    // La base sigue a la sesión: otro usuario, otra base.
    this.auth.currentUser$.pipe(takeUntilDestroyed(destroyRef))
      .subscribe(() => { void this.usarBaseDeLaSesion(); });
  }

  /** El nombre de la base local de un usuario en un tenant. */
  static nombreBase(tenant: string, usuarioId: number | string): string {
    return `softrent-offline:v1:${tenant}:${usuarioId}`;
  }

  // ── Operaciones ────────────────────────────────────────────────────────

  /**
   * Guarda el cambio y lo intenta mandar. Devuelve el `clientOpId`, que es lo
   * que la pantalla usa para seguirlo. Nunca falla por falta de señal.
   */
  async encolar(nueva: NuevaOperacion): Promise<string> {
    const base = await this.baseActual();
    const operacionesExistentes = await this.listarOperaciones(base);
    const ultimaPersistida = operacionesExistentes.reduce(
      (max, operacion) => Math.max(max, operacion.creadaEn || 0), 0);
    const ahora = Date.now();
    ExpedienteOfflineService.ultimaCreacion = Math.max(
      ahora,
      ExpedienteOfflineService.ultimaCreacion + 1,
      ultimaPersistida + 1);

    const op: OperacionPendiente = {
      clientOpId: nueva.clientOpId ?? crypto.randomUUID(),
      metodo: nueva.metodo,
      ruta: nueva.ruta,
      cuerpo: nueva.cuerpo,
      archivo: nueva.archivo,
      campos: nueva.campos,
      descripcion: nueva.descripcion,
      ref: nueva.ref,
      luego: nueva.luego,
      luegoFotos: nueva.luegoFotos,
      operadorId: this.operador.idActual() ?? undefined,
      creadaEn: ExpedienteOfflineService.ultimaCreacion,
      intentos: 0,
      estado: 'pendiente',
    };
    await this.guardarOperacion(base, op);
    this.pedirPersistencia();
    await this.recargar();
    void this.sincronizar();
    return op.clientOpId;
  }

  /**
   * Manda lo pendiente, en orden. Nunca hay dos vueltas a la vez: mandarían la
   * misma operación dos veces. Si ya hay una en curso, quien llama espera a que
   * termine. Pero esa vuelta ya leyó su lista: lo que se encoló después no va
   * en ella, así que en ese caso —y solo en ese— se pide una vuelta más. Repetir
   * una vuelta que no tiene nada nuevo saltaría la espera creciente del reintento.
   */
  sincronizar(): Promise<void> {
    if (this.enCurso) {
      if (!this.hayPendientesFueraDeLaVuelta()) return this.enCurso;
      this.siguiente ??= this.enCurso.catch(() => undefined).then(() => { this.siguiente = null; return this.sincronizar(); });
      return this.siguiente;
    }
    this.enCurso = this.vuelta().finally(() => { this.enCurso = null; });
    return this.enCurso;
  }

  private hayPendientesFueraDeLaVuelta(): boolean {
    return this.operaciones().some(o => o.estado === 'pendiente' && !this.enLaVuelta.has(o.clientOpId));
  }

  /**
   * Lo que responde una lectura de la pantalla (la orden, el checklist) también
   * dice si el servidor está: status 0 es que no llegó. Así «Sin conexión» no
   * depende de que haya algo en la cola para enterarse.
   */
  informarLectura(error?: unknown): void {
    if (error === undefined) { this.marcarServidorResponde(); return; }
    if (error instanceof HttpErrorResponse && error.status === 0) {
      this._servidorResponde.set(false);
      this._ultimoFallo.set('red');
      this.programarReintento();
    }
  }

  /** Una rechazada vuelve a la cola (el técnico ya corrigió lo que faltaba). */
  async reintentar(clientOpId: string): Promise<void> {
    const base = await this.baseActual();
    const op = await this.leerOperacion(base, clientOpId);
    if (!op) return;
    await this.guardarOperacion(base, { ...op, estado: 'pendiente', error: undefined, intentos: 0 });
    await this.recargar();
    await this.sincronizar();
  }

  /**
   * Vuelve a poner en cola los cambios rechazados cuando la persona pulsa
   * explícitamente sincronizar. Las reglas de negocio pueden rechazarlos de
   * nuevo, pero nunca quedan ignorados detrás de un botón que parece no hacer
   * nada.
   */
  async reintentarRechazadas(): Promise<void> {
    const base = await this.baseActual();
    const rechazadas = (await this.listarOperaciones(base))
      .filter(op => op.estado === 'rechazada');
    for (const op of rechazadas) {
      await this.guardarOperacion(base, { ...op, estado: 'pendiente', error: undefined, intentos: 0 });
    }
    if (rechazadas.length) await this.recargar();
    await this.sincronizar();
  }

  /** Reemplaza el cuerpo de una operación rechazada sin perder su identidad. */
  async corregirRechazada(clientOpId: string, cuerpo: Record<string, unknown>): Promise<void> {
    const base = await this.baseActual();
    const op = await this.leerOperacion(base, clientOpId);
    if (op?.estado !== 'rechazada') return;
    await this.guardarOperacion(base, { ...op, cuerpo, estado: 'pendiente', error: undefined, intentos: 0 });
    await this.recargar();
    await this.sincronizar();
  }

  /** Sustituye un archivo rechazado sin perder la operación ni sus metadatos. */
  async reemplazarArchivoRechazado(clientOpId: string, file: File): Promise<void> {
    const base = await this.baseActual();
    const op = await this.leerOperacion(base, clientOpId);
    if (op?.estado !== 'rechazada' || !op.archivo) return;
    await this.guardarOperacion(base, {
      ...op,
      archivo: {
        ...op.archivo,
        blob: file,
        nombre: file.name,
      },
      estado: 'pendiente', error: undefined, intentos: 0,
    });
    await this.recargar();
    await this.sincronizar();
  }

  /** El técnico decide no mandar un cambio rechazado. Solo se descartan rechazadas. */
  async descartar(clientOpId: string): Promise<void> {
    const base = await this.baseActual();
    const op = await this.leerOperacion(base, clientOpId);
    if (op?.estado !== 'rechazada') return;
    await base.borrar(BaseLocal.OPERACIONES, clientOpId);
    await this.recargar();
  }

  // ── Borradores ─────────────────────────────────────────────────────────

  /**
   * Lo que se está escribiendo y todavía no se manda; sobrevive a cerrar y reabrir el navegador. Al cerrar sesión se
   * avisa que queda. Para guardar lo que llegó del servidor o una marca de la pantalla, {@link guardarCopia}.
   */
  async guardarBorrador(clave: string, valor: unknown): Promise<void> {
    const base = await this.baseActual();
    await base.guardar<Borrador>(BaseLocal.BORRADORES, { clave, valor, guardadoEn: Date.now(), tipo: 'borrador' });
    this.pedirPersistencia();
  }

  /**
   * Una copia local que no es trabajo sin enviar: lo que llegó del servidor (para mostrarlo sin señal) o una marca de
   * la pantalla. Se lee igual, con {@link leerBorrador}, pero no cuenta como pendiente al cerrar sesión: abrir una
   * orden no puede hacer que salir pida «sincronizar».
   */
  async guardarCopia(clave: string, valor: unknown): Promise<void> {
    const base = await this.baseActual();
    await base.guardar<Borrador>(BaseLocal.BORRADORES, { clave, valor, guardadoEn: Date.now(), tipo: 'copia' });
  }

  async leerBorrador<T>(clave: string): Promise<T | undefined> {
    const base = await this.baseActual();
    return (await base.leer<Borrador>(BaseLocal.BORRADORES, clave))?.valor as T | undefined;
  }

  async borrarBorrador(clave: string): Promise<void> {
    const base = await this.baseActual();
    await base.borrar(BaseLocal.BORRADORES, clave);
  }

  // ── Cerrar sesión ──────────────────────────────────────────────────────

  /** Lo que se perdería si esta persona cerrara sesión y nadie lo mandara. */
  async antesDeSalir(): Promise<{ pendientes: number; fotos: number; rechazadas: number; borradores: number }> {
    return ExpedienteOfflineService.resumen(await this.baseActual());
  }

  private static async resumen(base: BaseLocal) {
    const ops = await base.todos<OperacionPendiente>(BaseLocal.OPERACIONES);
    return {
      pendientes: ops.filter(o => o.estado === 'pendiente').length,
      fotos: ops.filter(o => o.archivo).length,
      rechazadas: ops.filter(o => o.estado === 'rechazada').length,
      borradores: (await base.todos<Borrador>(BaseLocal.BORRADORES))
        .filter(b => b.tipo ? b.tipo === 'borrador' : !esCopiaHeredada(b.clave)).length,
    };
  }

  /**
   * Limpieza segura al cerrar sesión: la base de este usuario se borra solo si
   * no le queda nada sin mandar. Con pendientes o borradores se conserva; es
   * de este usuario, así que el próximo técnico no la ve, y vuelve a aparecer
   * cuando él entre de nuevo.
   *
   * <p>Se llama ANTES de soltar la sesión: toma la base de este usuario y la
   * cierra él, sin depender de que el usuario siga en el `AuthService`.
   */
  async alCerrarSesion(): Promise<boolean> {
    await this.abriendo;
    const base = this.base;
    if (!base) return false;
    const r = await ExpedienteOfflineService.resumen(base);
    const vacia = r.pendientes + r.rechazadas + r.borradores === 0;
    this.cancelarReintento();
    base.cerrar();
    this.base = null;
    this._operaciones.set([]);
    if (vacia) {
      await BaseLocal.eliminar(base.nombre);
    }
    return vacia;
  }

  // ── Internos ───────────────────────────────────────────────────────────

  /**
   * Pide que el navegador no borre esta base cuando le falte espacio (#864). Sin el pedido, Chrome en Android la deja en
   * «best-effort» y la puede vaciar con borradores y fotos sin enviar adentro. Se pide la primera vez que hay algo que cuidar
   * (no al abrir la sesión: un administrador en el escritorio no tiene por qué ver un permiso) y nunca detiene el trabajo: si el
   * navegador lo niega o no lo permite, todo sigue igual.
   */
  private pedirPersistencia(): void {
    if (this.persistenciaPedida) return;
    this.persistenciaPedida = true;
    const almacenamiento = typeof navigator === 'undefined' ? undefined : navigator.storage;
    if (!almacenamiento?.persist) return;
    void (async () => {
      try {
        if (await almacenamiento.persisted?.()) return;
        await almacenamiento.persist();
      } catch {
        // Sin permiso la base sigue funcionando; solo queda expuesta a que el navegador la limpie si se llena.
      }
    })();
  }

  private async vuelta(): Promise<void> {
    // Quien guarda recarga la señal antes de pedir sincronizar: lo que ella muestra
    // al empezar es lo que esta vuelta va a leer.
    this.enLaVuelta = new Set(this.operaciones().filter(o => o.estado === 'pendiente').map(o => o.clientOpId));
    if (this._red() === 'sin-conexion') return;
    // El servidor no respondía y no hay nada que mandar: una sola consulta
    // liviana para saber si volvió. Con cola, el primer envío hace de sonda.
    if (!this._servidorResponde() && this.pendientes() === 0) {
      await this.sondear();
      return;
    }
    if (!this.base) return;
    const base = this.base;
    // Lo de otro mecánico espera a que vuelva: mandarlo con la sesión de quien tiene la tablet ahora lo atribuiría mal.
    const cola = (await this.listarOperaciones(base))
      .filter(o => o.estado === 'pendiente' && this.operador.puedeEnviar(o.operadorId))
      .sort((a, b) => a.creadaEn - b.creadaEn);
    cola.forEach(o => this.enLaVuelta.add(o.clientOpId));
    if (cola.length === 0) return;

    this._sincronizando.set(true);
    try {
      for (const op of cola) {
        if (this.base !== base) return; // cambió la sesión a mitad de camino
        const resultado = await this.enviar(op);
        if (resultado === 'ok') {
          await base.borrar(BaseLocal.OPERACIONES, op.clientOpId);
        } else if (resultado === 'reintentar') {
          await this.guardarOperacion(base, { ...op, intentos: op.intentos + 1 });
          this.programarReintento();
          return; // el orden importa: lo que sigue puede depender de esta
        } else {
          await this.guardarOperacion(base,
            { ...op, estado: 'rechazada', error: resultado.rechazo, intentos: op.intentos + 1 });
        }
      }
      this.espera = ExpedienteOfflineService.ESPERA_INICIAL_MS;
    } finally {
      this._sincronizando.set(false);
      if (this.base === base) await this.recargar();
    }
  }

  private async sondear(): Promise<void> {
    try {
      await firstValueFrom(this.http.sondear('/health'));
      this.marcarServidorResponde();
    } catch (e) {
      const status = e instanceof HttpErrorResponse ? e.status : 0;
      // Cualquier respuesta HTTP, aunque sea un error, prueba que el servidor está.
      if (status !== 0) this.marcarServidorResponde();
      else this.programarReintento();
    }
  }

  private marcarServidorResponde(): void {
    const volvio = !this._servidorResponde();
    this._servidorResponde.set(true);
    if (this._ultimoFallo() === 'red') this._ultimoFallo.set(null);
    if (volvio) {
      this.espera = ExpedienteOfflineService.ESPERA_INICIAL_MS;
      if (this.pendientes() > 0) void Promise.resolve().then(() => this.sincronizar());
    }
  }

  private async enviar(op: OperacionPendiente): Promise<'ok' | 'reintentar' | { rechazo: string }> {
    try {
      const respuesta = await firstValueFrom(this.http.enviarEnSegundoPlano(op.metodo, op.ruta, this.cuerpoDe(op)));
      if (op.luego) {
        const id = (respuesta.body as { data?: { id?: unknown } } | null)?.data?.id;
        if (typeof id !== 'number') return { rechazo: 'El servidor no confirmó la foto guardada. Vuelve a tomarla.' };
        await firstValueFrom(this.http.enviarEnSegundoPlano(op.luego.metodo, op.luego.ruta,
          { ...op.luego.cuerpo, [op.luego.campo]: id, clientOpId: `${op.clientOpId}-respuesta` }));
      }
      if (op.luegoFotos) {
        const id = (respuesta.body as { data?: { id?: unknown } } | null)?.data?.id;
        if (typeof id !== 'number') return { rechazo: 'El servidor no confirmó el hallazgo guardado. Vuelve a tomar las fotos.' };
        let numero = 0;
        for (const foto of op.luegoFotos.fotos) {
          numero++;
          const form = new FormData();
          form.append('archivo', foto.blob, foto.nombre);
          form.append('momento', 'EVIDENCIA');
          form.append(op.luegoFotos.campoId, String(id));
          if (foto.descripcion) form.append('descripcion', foto.descripcion);
          Object.entries(op.luegoFotos.campos ?? {}).forEach(([k, v]) => form.append(k, v));
          form.append('clientOpId', `${op.clientOpId}-foto${numero}`);
          await firstValueFrom(this.http.enviarEnSegundoPlano('POST', op.luegoFotos.ruta, form));
        }
      }
      this._servidorResponde.set(true);
      this._ultimoFallo.set(null);
      return 'ok';
    } catch (e) {
      const status = e instanceof HttpErrorResponse ? e.status : 0;
      if (status === 0) {
        // No llegó: sin salida a internet, o el servidor caído. No es «error».
        this._servidorResponde.set(false);
        this._ultimoFallo.set('red');
        return 'reintentar';
      }
      this._servidorResponde.set(true);
      // La sesión del mecánico venció mientras se enviaba: no es un rechazo del negocio. Espera su PIN.
      if (codigoDeOperador(e)) {
        this._ultimoFallo.set('sesion');
        return 'reintentar';
      }
      if (status === 401) {
        this._ultimoFallo.set('sesion');
        return 'reintentar';
      }
      if (status === 408 || status === 429 || status >= 500) {
        this._ultimoFallo.set('servidor');
        return 'reintentar';
      }
      const mensaje = e instanceof HttpErrorResponse
        ? (e.error?.message ?? e.error?.mensaje ?? e.message) : String(e);
      return { rechazo: mensaje || 'El servidor no aceptó este cambio.' };
    }
  }

  /** El `clientOpId` viaja siempre: en el JSON o como campo del multipart. */
  private cuerpoDe(op: OperacionPendiente): unknown {
    if (op.metodo === 'DELETE') return undefined;
    if (op.archivo) {
      const form = new FormData();
      form.append(op.archivo.campo, op.archivo.blob, op.archivo.nombre);
      Object.entries(op.campos ?? {}).forEach(([k, v]) => form.append(k, v));
      form.append('clientOpId', op.clientOpId);
      return form;
    }
    return { ...(op.cuerpo ?? {}), clientOpId: op.clientOpId };
  }

  private programarReintento(): void {
    this.cancelarReintento();
    const espera = this.espera;
    this.espera = Math.min(this.espera * 2, ExpedienteOfflineService.ESPERA_MAXIMA_MS);
    this.reintento = setTimeout(() => { this.reintento = null; void this.sincronizar(); }, espera);
  }

  private cancelarReintento(): void {
    if (this.reintento) {
      clearTimeout(this.reintento);
      this.reintento = null;
    }
  }

  private async guardarOperacion(base: BaseLocal, op: OperacionPendiente): Promise<void> {
    const archivo = op.archivo;
    const almacenada: OperacionAlmacenada = {
      ...op,
      archivo: archivo ? {
        campo: archivo.campo,
        blob: await archivo.blob.arrayBuffer(),
        tipo: archivo.blob.type,
        nombre: archivo.nombre,
      } : undefined,
      luegoFotos: op.luegoFotos ? {
        ruta: op.luegoFotos.ruta,
        campoId: op.luegoFotos.campoId,
        campos: op.luegoFotos.campos,
        fotos: await Promise.all(op.luegoFotos.fotos.map(async f => ({
          blob: await f.blob.arrayBuffer(), tipo: f.blob.type, nombre: f.nombre, descripcion: f.descripcion,
        }))),
      } : undefined,
    };
    await base.guardar(BaseLocal.OPERACIONES, almacenada);
  }

  private async leerOperacion(base: BaseLocal, id: string): Promise<OperacionPendiente | undefined> {
    const op = await base.leer<OperacionAlmacenada | OperacionPendiente>(BaseLocal.OPERACIONES, id);
    return op ? this.hidratarOperacion(op) : undefined;
  }

  private async listarOperaciones(base: BaseLocal): Promise<OperacionPendiente[]> {
    const operaciones = await base.todos<OperacionAlmacenada | OperacionPendiente>(BaseLocal.OPERACIONES);
    return operaciones.map(op => this.hidratarOperacion(op));
  }

  /** Rehidrata también blobs guardados por versiones previas de la cola. */
  private hidratarOperacion(op: OperacionAlmacenada | OperacionPendiente): OperacionPendiente {
    let hidratada = op as OperacionPendiente;
    const archivo = op.archivo;
    if (archivo && !(archivo.blob instanceof Blob)) {
      const guardado = archivo as OperacionAlmacenada['archivo'];
      if (guardado) {
        hidratada = {
          ...op,
          archivo: { campo: guardado.campo, blob: new Blob([guardado.blob], { type: guardado.tipo }), nombre: guardado.nombre },
        } as OperacionPendiente;
      }
    }
    const fotos = op.luegoFotos;
    if (fotos && fotos.fotos.some(f => !(f.blob instanceof Blob))) {
      hidratada = {
        ...hidratada,
        luegoFotos: {
          ruta: fotos.ruta, campoId: fotos.campoId, campos: fotos.campos,
          fotos: (fotos.fotos as unknown as { blob: ArrayBuffer | Blob; tipo?: string; nombre: string; descripcion?: string }[]).map(f => ({
            blob: f.blob instanceof Blob ? f.blob : new Blob([f.blob], { type: f.tipo }), nombre: f.nombre, descripcion: f.descripcion,
          })),
        },
      };
    }
    return hidratada;
  }

  private async recargar(): Promise<void> {
    if (!this.base) { this._operaciones.set([]); return; }
    const ops = await this.listarOperaciones(this.base);
    this._operaciones.set(ops.sort((a, b) => a.creadaEn - b.creadaEn));
  }

  private async baseActual(): Promise<BaseLocal> {
    await this.usarBaseDeLaSesion();
    if (!this.base) {
      throw new Error('Sin sesión no hay base local: el expediente offline es por usuario.');
    }
    return this.base;
  }

  /** Abre la base del usuario de la sesión; si cambió, cierra la anterior. */
  private usarBaseDeLaSesion(): Promise<void> {
    const usuario = this.auth.getUserId();
    const nombre = usuario != null
      ? ExpedienteOfflineService.nombreBase(window.location.hostname, usuario) : null;
    if (this.base?.nombre === nombre) return this.abriendo ?? Promise.resolve();

    const abrir = async () => {
      if (this.base?.nombre === nombre) return;
      this.cancelarReintento();
      this.base?.cerrar();
      this.base = null;
      this._operaciones.set([]);
      if (nombre) {
        this.base = await BaseLocal.abrir(nombre);
        await this.recargar();
        void this.sincronizar();
      }
    };
    this.abriendo = (this.abriendo ?? Promise.resolve()).then(abrir, abrir);
    return this.abriendo;
  }
}
